import { useEffect, useState } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import {
  Users, Monitor, Smartphone, Tablet, TrendingUp, Eye, Calendar, Globe, RefreshCw,
} from "lucide-react";
import { api } from "../../lib/api";
import { usePrefs } from "../../context/PrefsContext";
import { toast } from "sonner";

const DEVICE_COLORS = { desktop: "#f97316", mobile: "#0ea5e9", tablet: "#a855f7" };

const PAGE_LABELS = {
  "/": "Halaman Utama · Home",
  "/tentang-kami": "Tentang Kami · About Us",
  "/pasukan-kami": "Pasukan Kami · Our People",
  "/perkhidmatan": "Perkhidmatan · Our Services",
  "/galeri": "Galeri · Gallery",
  "/hubungi": "Hubungi Kami · Contact Us",
};
const pageLabel = (path) => PAGE_LABELS[path] || path;
const isTestPath = (path) => /^\/test/i.test(path || "");

const StatCard = ({ icon: Icon, label, value, sub, accent = "orange", testId }) => {
  const grad = {
    orange: "from-orange-500/10 to-orange-500/[0.03] border-orange-500/20 dark:from-orange-500/20 dark:to-orange-500/5",
    blue: "from-sky-500/10 to-sky-500/[0.03] border-sky-500/20 dark:from-sky-500/20 dark:to-sky-500/5",
    green: "from-emerald-500/10 to-emerald-500/[0.03] border-emerald-500/20 dark:from-emerald-500/20 dark:to-emerald-500/5",
    purple: "from-purple-500/10 to-purple-500/[0.03] border-purple-500/20 dark:from-purple-500/20 dark:to-purple-500/5",
  }[accent];
  const iconClr = {
    orange: "text-orange-600 dark:text-orange-400 bg-orange-500/15 border-orange-500/25",
    blue: "text-sky-600 dark:text-sky-400 bg-sky-500/15 border-sky-500/25",
    green: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/15 border-emerald-500/25",
    purple: "text-purple-600 dark:text-purple-400 bg-purple-500/15 border-purple-500/25",
  }[accent];
  return (
    <div data-testid={testId} className={`bg-white dark:bg-gradient-to-br ${grad} border rounded-2xl p-5 relative overflow-hidden shadow-sm dark:shadow-none transition-colors`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-widest">{label}</p>
          <p className="text-slate-900 dark:text-white font-heading font-extrabold text-3xl mt-2 tabular-nums">{value}</p>
          {sub && <p className="text-slate-500 dark:text-slate-500 text-xs mt-1 truncate">{sub}</p>}
        </div>
        <span className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${iconClr}`}>
          <Icon size={20} />
        </span>
      </div>
    </div>
  );
};

const Card = ({ title, subtitle, right, children, testId }) => (
  <div data-testid={testId} className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-5 lg:p-6 shadow-sm dark:shadow-none transition-colors">
    <div className="flex items-start justify-between gap-2 mb-4">
      <div>
        <h3 className="font-heading font-semibold text-slate-900 dark:text-white text-base">{title}</h3>
        {subtitle && <p className="text-slate-500 dark:text-slate-500 text-xs mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("ms-MY", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

export const AnalyticsDashboard = () => {
  const { theme } = usePrefs();
  const isDark = theme === "dark";
  const chartGrid = isDark ? "#1f2937" : "#e2e8f0";
  const chartAxis = isDark ? "#334155" : "#cbd5e1";
  const chartText = isDark ? "#94a3b8" : "#64748b";
  const tooltipStyle = {
    background: isDark ? "#0f172a" : "#ffffff",
    border: isDark ? "1px solid #334155" : "1px solid #e2e8f0",
    borderRadius: 8,
    color: isDark ? "#fff" : "#0f172a",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const { data } = await api.get("/analytics/summary");
      setData(data);
    } catch (err) {
      toast.error("Gagal memuat analitik");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="text-slate-500 dark:text-slate-400 text-center py-16" data-testid="analytics-loading">Memuatkan analitik...</div>;
  }
  if (!data) return null;

  const totalDevices = (data.devices.desktop || 0) + (data.devices.mobile || 0) + (data.devices.tablet || 0);
  const pct = (n) => (totalDevices ? Math.round((n / totalDevices) * 100) : 0);
  const deviceData = [
    { name: "Desktop", value: data.devices.desktop || 0, color: DEVICE_COLORS.desktop },
    { name: "Mobile", value: data.devices.mobile || 0, color: DEVICE_COLORS.mobile },
    { name: "Tablet", value: data.devices.tablet || 0, color: DEVICE_COLORS.tablet },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-slate-900 dark:text-white text-2xl lg:text-3xl tracking-tight">Dashboard Analitik</h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Data pelawat laman untuk 30 hari terakhir. Auto-tracking direkod dari IP & pelayar sebenar setiap kali seseorang muat halaman.</p>
        </div>
        <button
          onClick={() => load(true)}
          data-testid="analytics-refresh-btn"
          disabled={refreshing}
          className="flex items-center gap-2 text-sm font-semibold bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 hover:border-orange-500/50 text-slate-700 dark:text-slate-200 px-4 py-2 rounded-full transition-colors disabled:opacity-60 shadow-sm dark:shadow-none"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Muat Semula
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-today" icon={Calendar} label="Hari Ini" value={data.totals.today} accent="orange" />
        <StatCard testId="stat-last7" icon={TrendingUp} label="7 Hari Lepas" value={data.totals.last_7_days} accent="green" />
        <StatCard testId="stat-last30" icon={Eye} label="30 Hari Lepas" value={data.totals.last_30_days} accent="blue" />
        <StatCard testId="stat-unique" icon={Users} label="Unik (30 Hari)" value={data.totals.unique_last_30} sub={`Sepanjang masa: ${data.totals.all_time}`} accent="purple" />
      </div>

      <Card title="Trend Pelawat Harian" subtitle="Bilangan lawatan setiap hari (30 hari terakhir)" testId="chart-daily">
        <div className="h-72">
          <ResponsiveContainer>
            <LineChart data={data.daily} margin={{ top: 5, right: 15, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
              <XAxis dataKey="date" tick={{ fill: chartText, fontSize: 11 }} axisLine={{ stroke: chartAxis }} tickLine={false} />
              <YAxis tick={{ fill: chartText, fontSize: 11 }} axisLine={{ stroke: chartAxis }} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="visits" stroke="#f97316" strokeWidth={2.5} dot={{ fill: "#f97316", r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Lawatan Bulanan" subtitle="12 bulan terakhir" testId="chart-monthly">
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.monthly} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                <XAxis dataKey="month" tick={{ fill: chartText, fontSize: 10 }} axisLine={{ stroke: chartAxis }} tickLine={false} />
                <YAxis tick={{ fill: chartText, fontSize: 11 }} axisLine={{ stroke: chartAxis }} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: isDark ? "rgba(249,115,22,0.08)" : "rgba(249,115,22,0.06)" }} />
                <Bar dataKey="visits" fill="#f97316" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Pecahan Peranti" subtitle="30 hari terakhir" testId="chart-devices">
          {deviceData.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-12">Tiada data peranti lagi.</p>
          ) : (
            <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
              <div className="h-48">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={deviceData} innerRadius={45} outerRadius={80} paddingAngle={3} dataKey="value">
                      {deviceData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2.5" data-testid="device-desktop">
                  <Monitor size={14} className="text-orange-500" />
                  <span className="text-slate-700 dark:text-slate-300 font-medium">Desktop</span>
                  <span className="text-slate-900 dark:text-white font-bold ml-auto tabular-nums">{pct(data.devices.desktop)}%</span>
                </li>
                <li className="flex items-center gap-2.5" data-testid="device-mobile">
                  <Smartphone size={14} className="text-sky-500" />
                  <span className="text-slate-700 dark:text-slate-300 font-medium">Mobile</span>
                  <span className="text-slate-900 dark:text-white font-bold ml-auto tabular-nums">{pct(data.devices.mobile)}%</span>
                </li>
                <li className="flex items-center gap-2.5" data-testid="device-tablet">
                  <Tablet size={14} className="text-purple-500" />
                  <span className="text-slate-700 dark:text-slate-300 font-medium">Tablet</span>
                  <span className="text-slate-900 dark:text-white font-bold ml-auto tabular-nums">{pct(data.devices.tablet)}%</span>
                </li>
              </ul>
            </div>
          )}
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Halaman Popular" subtitle="10 halaman paling banyak dilawati" testId="top-pages">
          {data.top_pages.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-8">Tiada data lagi. Data akan muncul apabila pelawat mula datang.</p>
          ) : (
            <ul className="space-y-2">
              {data.top_pages.filter((p) => !isTestPath(p.path)).map((p, i) => (
                <li key={p.path} className="flex items-center gap-3 bg-slate-50 dark:bg-slate-950/40 border border-slate-200/60 dark:border-white/5 rounded-lg px-3 py-2.5">
                  <span className="w-6 h-6 rounded-md bg-orange-500/20 text-orange-600 dark:text-orange-400 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-slate-800 dark:text-slate-200 text-sm font-medium truncate">{pageLabel(p.path)}</span>
                    <span className="block text-slate-400 dark:text-slate-500 text-[11px] font-mono truncate">{p.path}</span>
                  </span>
                  <span className="text-slate-900 dark:text-white font-bold text-sm tabular-nums">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Pelayar (Browser)" subtitle="30 hari terakhir" testId="browsers">
          {data.browsers.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-8">Tiada data lagi.</p>
          ) : (
            <ul className="space-y-2.5">
              {data.browsers.map((b) => {
                const max = Math.max(...data.browsers.map((x) => x.count));
                const width = max ? Math.round((b.count / max) * 100) : 0;
                return (
                  <li key={b.name} className="text-sm">
                    <div className="flex justify-between text-slate-700 dark:text-slate-300 mb-1">
                      <span className="flex items-center gap-2"><Globe size={12} className="text-orange-500" /> {b.name}</span>
                      <span className="text-slate-900 dark:text-white font-bold tabular-nums">{b.count}</span>
                    </div>
                    <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all" style={{ width: `${width}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <Card title="Lawatan Terkini" subtitle="15 aktiviti terkini pelawat" testId="recent-visits">
        {data.recent.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-8">Belum ada lawatan.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-500 text-xs font-semibold uppercase tracking-wider">
                  <th className="px-2 py-2">Masa</th>
                  <th className="px-2 py-2">Halaman</th>
                  <th className="px-2 py-2">Peranti</th>
                  <th className="px-2 py-2">Pelayar</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.filter((r) => !isTestPath(r.path)).map((r) => {
                  const DevIcon = r.device === "mobile" ? Smartphone : r.device === "tablet" ? Tablet : Monitor;
                  return (
                    <tr key={r.id} className="border-t border-slate-200/60 dark:border-white/5 text-slate-700 dark:text-slate-300">
                      <td className="px-2 py-2.5 whitespace-nowrap">{formatDate(r.created_at)}</td>
                      <td className="px-2 py-2.5 text-xs font-medium text-orange-600 dark:text-orange-400 max-w-[240px] truncate">{pageLabel(r.path)}</td>
                      <td className="px-2 py-2.5"><span className="inline-flex items-center gap-1.5"><DevIcon size={13} className="text-slate-400" /> {r.device}</span></td>
                      <td className="px-2 py-2.5 text-slate-500 dark:text-slate-400">{r.browser}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
